import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import SEO from '../../components/SEO';
import { 
  ArrowRight, CheckCircle, TrendingUp, Star,
  Shield, Target, Bell, Brain, Zap, Award, Trophy,
  ChevronRight, Sparkles, BarChart3, Users, Clock,
  AlertTriangle, Eye, Lock
} from 'lucide-react';

// FAQ for SEO - Conversion odaklı sorular
const faqData = [
  {
    question: "En iyi kripto sinyal platformu nasil secilir?",
    answer: "En iyi kripto sinyal platformunu secerken seffaflik, dogrulanabilir performans ve risk yonetimi kritik faktorlerdir. Platformun gecmis sinyal sonuclarini acik paylasip paylasmadigi, stop-loss kullaniminini zorunlu tutup tutmadigi ve bagimsiz dogrulama sunup sunmadigi kontrol edilmelidir."
  },
  {
    question: "Kripto sinyalleri gercekten ise yariyor mu?",
    answer: "Hicbir sinyal sistemi yuzde yuz basari garantisi veremez. Onemli olan tutarli performans ve risk yonetimidir. Teknik analiz tabanli sinyal sistemleri, piyasa kosullarini analiz ederek olasilik bazli senaryolar uretir. Gecmis performans gelecek sonuclari garanti etmez."
  },
  {
    question: "Ucretsiz kripto sinyalleri guvenilir mi?",
    answer: "Telegram'daki ucretsiz sinyal gruplarinin cogu seffaf degildir ve pump-dump riski tasir. Guvenilir bir sinyal platformu secerken, ucretsiz veya ucretli farketmeksizin sinyallerin ayni algoritmadan gelip gelmedigini ve performansin seffaf paylasilip paylasilmadigini kontrol edin."
  },
  {
    question: "Kripto sinyal platformlari ne kadar ucret aliyor?",
    answer: "Kripto sinyal platformlarinin ucretleri degiskendir. Bazi platformlar ucretsiz plan sunarken, profesyonel planlar genellikle aylik 10-50 dolar veya TL karsiligidir. Secim yaparken ucret disinda seffaflik, performans gecmisi ve risk yonetimi kriterlerini de degerlendirin."
  },
  {
    question: "Hangi borsalarda kripto sinyalleri kullanilabilir?",
    answer: "Kripto sinyalleri genel olarak tum buyuk borsalarda kullanilabilir: Binance, Bybit, OKX, KuCoin ve digerlerinde islem yapabilirsiniz. Kaliteli sinyal platformlari borsaniza baglanmaz ve API anahtari istemez."
  },
  {
    question: "Gunde kac kripto sinyali uretilir?",
    answer: "Sinyal sayisi piyasa kosullarina baglidir. Kaliteli sinyal sistemleri ortalama gunde 2-5 sinyal uretir. Kalite, kantiteden once gelir ve sadece yuksek skorlu setuplar sinyal olarak yayinlanir."
  }
];

const structuredData = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqData.map(item => ({
    "@type": "Question",
    "name": item.question,
    "acceptedAnswer": { "@type": "Answer", "text": item.answer }
  }))
};

// Karşılaştırma verileri
const comparisonData = [
  { feature: "Şeffaf Performans", borsamex: true, others: false },
  { feature: "Doğrulanabilir Sonuçlar", borsamex: true, others: false },
  { feature: "Risk Yönetimi (Stop-Loss)", borsamex: true, others: "Nadiren" },
  { feature: "7/24 Otomatik Tarama", borsamex: true, others: "Nadiren" },
  { feature: "Ücretsiz Deneme", borsamex: true, others: "Nadiren" },
  { feature: "Telegram Bildirimleri", borsamex: true, others: true },
  { feature: "API Key Gerektirmez", borsamex: true, others: false },
];

const EnIyiKriptoSinyalleriPage = () => {
  return (
    <div className="min-h-screen bg-brand-dark">
      <SEO 
        title="En İyi Kripto Sinyalleri 2026 – Karşılaştırma ve İnceleme | Borsamex"
        description="En iyi kripto sinyal platformlarını karşılaştırın. Şeffaf performans, doğrulanabilir sonuçlar ve risk yönetimi ile Borsamex farkını keşfedin. Ücretsiz deneyin."
        keywords="en iyi kripto sinyalleri, kripto sinyal platformu, kripto sinyal karşılaştırma, en güvenilir kripto sinyalleri, kripto sinyal önerileri, bitcoin sinyal, altcoin sinyal"
        canonical="https://borsamex.com/en-iyi-kripto-sinyalleri"
        structuredData={structuredData}
      />

      {/* HERO */}
      <section className="py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-amber-500/5 via-transparent to-brand-green/5" />
        <div className="container mx-auto px-4 max-w-5xl relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
            <Badge className="mb-6 px-4 py-2 bg-amber-500/10 text-amber-400 border-amber-500/20">
              <Trophy className="h-4 w-4 mr-2" />
              2026 Karşılaştırma Rehberi
            </Badge>
            
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-white mb-6 leading-tight">
              En İyi Kripto Sinyalleri<br />
              <span className="text-brand-green">Hangisini Seçmelisiniz?</span>
            </h1>
            
            <p className="text-lg md:text-xl text-slate-300 mb-8 max-w-3xl mx-auto">
              Kripto sinyal platformlarını karşılaştırırken <strong className="text-white">şeffaflık</strong>, 
              <strong className="text-white"> doğrulanabilir performans</strong> ve 
              <strong className="text-white"> risk yönetimi</strong> en kritik faktörlerdir. 
              İşte bilmeniz gerekenler.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register">
                <Button size="lg" className="bg-brand-green hover:bg-brand-green/90 text-brand-dark font-bold px-8">
                  Ücretsiz Dene <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/performans">
                <Button size="lg" variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800">
                  <Eye className="mr-2 h-5 w-5" /> Performansı Gör
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Neden Önemli */}
      <section className="py-16 bg-slate-900/50">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Kripto Sinyal Platformu Seçerken Nelere Dikkat Etmeli?
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Telegram'da yüzlerce "ücretsiz sinyal" grubu var. Ama çoğu şeffaf değil ve pump-dump riski taşıyor.
              İşte dikkat etmeniz gereken kritik faktörler:
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center mb-4">
                  <AlertTriangle className="h-6 w-6 text-red-400" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">Şeffaflık Eksikliği</h3>
                <p className="text-slate-400 text-sm">
                  Çoğu platform sadece kazanan sinyalleri gösterir, kaybedenleri gizler. 
                  Bu, gerçek performansı değerlendirmenizi imkansız kılar.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center mb-4">
                  <AlertTriangle className="h-6 w-6 text-red-400" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">Risk Yönetimi Yok</h3>
                <p className="text-slate-400 text-sm">
                  "Al" deyip bırakan platformlar tehlikelidir. Stop-loss ve hedef seviyeleri 
                  olmadan trade yapmak kumardır.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center mb-4">
                  <AlertTriangle className="h-6 w-6 text-red-400" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">Pump & Dump Riski</h3>
                <p className="text-slate-400 text-sm">
                  Bazı gruplar önce pozisyon alıp sonra sinyal vererek sizi exit likidite olarak kullanır. 
                  Küçük coinlerde bu çok yaygın.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Karşılaştırma Tablosu */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Borsamex vs Diğer Platformlar
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Objektif karşılaştırma. Kararı siz verin.
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left py-4 px-4 text-slate-400 font-medium">Özellik</th>
                  <th className="text-center py-4 px-4">
                    <span className="text-brand-green font-bold">Borsamex</span>
                  </th>
                  <th className="text-center py-4 px-4 text-slate-400">Diğerleri</th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row, idx) => (
                  <tr key={idx} className="border-b border-slate-800">
                    <td className="py-4 px-4 text-white">{row.feature}</td>
                    <td className="py-4 px-4 text-center">
                      {row.borsamex === true ? (
                        <CheckCircle className="h-5 w-5 text-brand-green mx-auto" />
                      ) : (
                        <span className="text-slate-500">—</span>
                      )}
                    </td>
                    <td className="py-4 px-4 text-center">
                      {row.others === true ? (
                        <CheckCircle className="h-5 w-5 text-slate-500 mx-auto" />
                      ) : row.others === false ? (
                        <span className="text-red-400">✕</span>
                      ) : (
                        <span className="text-amber-400 text-sm">{row.others}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Borsamex Farkı */}
      <section className="py-16 bg-slate-900/50">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-12">
            <Badge className="mb-4 px-4 py-2 bg-brand-green/10 text-brand-green border-brand-green/20">
              <Sparkles className="h-4 w-4 mr-2" />
              Borsamex Farkı
            </Badge>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Neden Borsamex?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="w-10 h-10 rounded-lg bg-brand-green/10 flex items-center justify-center flex-shrink-0">
                  <Eye className="h-5 w-5 text-brand-green" />
                </div>
                <div>
                  <h3 className="text-white font-bold mb-1">%100 Şeffaf Performans</h3>
                  <p className="text-slate-400 text-sm">
                    Tüm sinyaller - kazanan ve kaybeden - açıkça gösterilir. 
                    <Link to="/performans" className="text-brand-green hover:underline ml-1">Performans sayfasında</Link> her şey açık.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-10 h-10 rounded-lg bg-brand-green/10 flex items-center justify-center flex-shrink-0">
                  <Shield className="h-5 w-5 text-brand-green" />
                </div>
                <div>
                  <h3 className="text-white font-bold mb-1">Risk Yönetimi Dahil</h3>
                  <p className="text-slate-400 text-sm">
                    Her sinyalde giriş, stop-loss ve 3 hedef seviyesi net olarak belirtilir. 
                    Riski siz kontrol edersiniz.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-10 h-10 rounded-lg bg-brand-green/10 flex items-center justify-center flex-shrink-0">
                  <Lock className="h-5 w-5 text-brand-green" />
                </div>
                <div>
                  <h3 className="text-white font-bold mb-1">Borsanıza Bağlanmıyoruz</h3>
                  <p className="text-slate-400 text-sm">
                    API key istemiyoruz, fonlarınıza erişimimiz yok. 
                    Sadece analiz ve sinyal sağlıyoruz - kararları siz verirsiniz.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="w-10 h-10 rounded-lg bg-brand-green/10 flex items-center justify-center flex-shrink-0">
                  <Brain className="h-5 w-5 text-brand-green" />
                </div>
                <div>
                  <h3 className="text-white font-bold mb-1">AI + Teknik Analiz</h3>
                  <p className="text-slate-400 text-sm">
                    Sniper Engine 200+ göstergeyi analiz eder, GPT-4o piyasa yorumu ekler. 
                    <Link to="/ai-kripto-sinyalleri" className="text-brand-green hover:underline ml-1">AI sinyalleri hakkında</Link>
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-10 h-10 rounded-lg bg-brand-green/10 flex items-center justify-center flex-shrink-0">
                  <Clock className="h-5 w-5 text-brand-green" />
                </div>
                <div>
                  <h3 className="text-white font-bold mb-1">7/24 Otomatik Tarama</h3>
                  <p className="text-slate-400 text-sm">
                    Siz uyurken bile piyasa taranır. Fırsat oluştuğunda anında bildirim alırsınız.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-10 h-10 rounded-lg bg-brand-green/10 flex items-center justify-center flex-shrink-0">
                  <Zap className="h-5 w-5 text-brand-green" />
                </div>
                <div>
                  <h3 className="text-white font-bold mb-1">Ücretsiz Başlayın</h3>
                  <p className="text-slate-400 text-sm">
                    Kredi kartı gerekmez. Ücretsiz plan ile sistemi test edin, beğenirseniz yükseltin.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Performans Özeti */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Gerçek Performans Verileri
            </h2>
            <p className="text-slate-400">
              Şeffaflık ilkemiz gereği tüm veriler herkese açık.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-6 text-center">
                <div className="text-3xl font-bold text-brand-green mb-1">1000+</div>
                <div className="text-slate-400 text-sm">Toplam Sinyal</div>
              </CardContent>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-6 text-center">
                <div className="text-3xl font-bold text-white mb-1">%68+</div>
                <div className="text-slate-400 text-sm">TP1 Başarı</div>
              </CardContent>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-6 text-center">
                <div className="text-3xl font-bold text-white mb-1">1:1.9</div>
                <div className="text-slate-400 text-sm">Profit Factor</div>
              </CardContent>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-6 text-center">
                <div className="text-3xl font-bold text-white mb-1">7/24</div>
                <div className="text-slate-400 text-sm">Aktif Tarama</div>
              </CardContent>
            </Card>
          </div>

          <div className="text-center">
            <Link to="/performans">
              <Button variant="outline" className="border-brand-green text-brand-green hover:bg-brand-green/10">
                Detaylı Performans Analizi <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* İlgili Sayfalar */}
      <section className="py-16 bg-slate-900/50">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Kripto Sinyalleri Hakkında
            </h2>
            <p className="text-slate-400">
              Daha fazla bilgi için rehber içeriklerimizi inceleyin.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <Link to="/rehber/kripto-sinyali-nedir">
              <Card className="bg-slate-800/50 border-slate-700 hover:border-brand-green/50 transition-colors h-full">
                <CardContent className="p-6">
                  <h3 className="text-white font-bold mb-2">Kripto Sinyali Nedir?</h3>
                  <p className="text-slate-400 text-sm">
                    Kripto sinyallerinin ne olduğunu, nasıl çalıştığını ve nasıl kullanılacağını öğrenin.
                  </p>
                  <span className="text-brand-green text-sm mt-4 inline-flex items-center">
                    Oku <ChevronRight className="h-4 w-4 ml-1" />
                  </span>
                </CardContent>
              </Card>
            </Link>

            <Link to="/rehber/kripto-sinyal-okuma">
              <Card className="bg-slate-800/50 border-slate-700 hover:border-brand-green/50 transition-colors h-full">
                <CardContent className="p-6">
                  <h3 className="text-white font-bold mb-2">Sinyal Nasıl Okunur?</h3>
                  <p className="text-slate-400 text-sm">
                    Entry, TP, SL kavramları ve bir sinyali doğru yorumlama rehberi.
                  </p>
                  <span className="text-brand-green text-sm mt-4 inline-flex items-center">
                    Oku <ChevronRight className="h-4 w-4 ml-1" />
                  </span>
                </CardContent>
              </Card>
            </Link>

            <Link to="/rehber/risk-yonetimi">
              <Card className="bg-slate-800/50 border-slate-700 hover:border-brand-green/50 transition-colors h-full">
                <CardContent className="p-6">
                  <h3 className="text-white font-bold mb-2">Risk Yönetimi</h3>
                  <p className="text-slate-400 text-sm">
                    Sermaye koruma, pozisyon boyutlandırma ve kayıp yönetimi stratejileri.
                  </p>
                  <span className="text-brand-green text-sm mt-4 inline-flex items-center">
                    Oku <ChevronRight className="h-4 w-4 ml-1" />
                  </span>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Sıkça Sorulan Sorular
            </h2>
          </div>

          <div className="space-y-4">
            {faqData.map((faq, idx) => (
              <Card key={idx} className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-6">
                  <h3 className="text-white font-bold mb-2">{faq.question}</h3>
                  <p className="text-slate-400 text-sm">{faq.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-b from-brand-green/10 to-transparent">
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Şeffaf Performans, Gerçek Sonuçlar
          </h2>
          <p className="text-slate-300 mb-8">
            Ücretsiz hesap oluşturun ve Borsamex farkını kendiniz görün. 
            Kredi kartı gerekmez.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button size="lg" className="bg-brand-green hover:bg-brand-green/90 text-brand-dark font-bold px-8">
                Ücretsiz Başla <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/signals">
              <Button size="lg" variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800">
                Sinyalleri İncele
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EnIyiKriptoSinyalleriPage;
